import React, { useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import WalledContent from 'components/Common/WalledContent'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import { useBedbankPackageViewsForIndividualRooms } from 'hooks/useBedbankPackageViews'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCheapestPackagesByRoom } from 'selectors/offerPage/bedbankMultipleRoomBookingSelectors'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import BedbankSummaryPriceDetailsIndividualRooms, { RoomWithSummaryData } from './BedbankSummaryPriceDetails/BedbankSummaryPriceDetailsIndividualRooms'
import BedbankSummaryPriceDetailsSameRoom from './BedbankSummaryPriceDetails/BedbankSummaryPriceDetailsSameRoom'
import useBedbankRates from 'hooks/useBedbankRates'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'

const SummaryPane = styled(Pane)`
  position: relative;
  min-width: ${rem(342)};
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  checkInDate?: string
  checkOutDate?: string
  className?: string
  offer: App.BedbankOffer
  onViewPackageOptions?: () => void
  onCheckAvailabilityClick?: () => void
  rooms: Array<OfferPageRoom>
  flightPrice?: number
  flightsEnabled: boolean
  duration: number
  loadingPrices?: boolean
  canUseMultiRoomUI: boolean
}

function BedbankOfferSummary(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    className,
    offer,
    onViewPackageOptions,
    onCheckAvailabilityClick,
    rooms,
    loadingPrices,
    flightsEnabled,
    duration,
    flightPrice,
    canUseMultiRoomUI,
  } = props
  const priceTrackedRef = useRef(false)

  const offerPageState = useContext(OfferPageStateContext)

  // Multi-room
  const [packageViewsByRoom, packageViewsLoading] = useBedbankPackageViewsForIndividualRooms(offer, rooms)
  const cheapestPackageSelectionsByRoom = useAppSelector(appState => getCheapestPackagesByRoom(offerPageState, appState))

  const roomsWithData = useMemo(() => {
    if (packageViewsLoading || !canUseMultiRoomUI) {
      return
    }
    const rwd: Array<RoomWithSummaryData> = []

    rooms.forEach((room, i) => {
      const { package: hotelOnlyPkg, rate: hotelOnlyRate } = cheapestPackageSelectionsByRoom.hotelOnly[room.id] ?? {}
      const { package: bundlePkg, rate: bundleRate } = cheapestPackageSelectionsByRoom.bundle[room.id] ?? {}
      if (!hotelOnlyPkg || !bundlePkg) { return }
      const packageViews = packageViewsByRoom[i]

      const hotelOnlyPackageView = packageViews?.find(pv => pv.package.roomId === hotelOnlyPkg?.roomId)
      if (!hotelOnlyPackageView) { return }
      const hotelOnlySell = hotelOnlyPkg.sell
      if (!hotelOnlyRate && !hotelOnlySell) { return }
      const hotelOnlyPromotion = hotelOnlyPackageView.promotions[hotelOnlyRate?.id ?? '']

      const bundlePackageView = packageViews?.find(pv => pv.package.roomId === bundlePkg?.roomId)
      if (!bundlePackageView) { return }
      const bundleSell = bundlePkg.sell
      if (!bundleRate && !bundleSell) { return }
      const bundlePromotion = hotelOnlyPackageView.promotions[bundleRate?.id ?? '']

      rwd.push({
        room,
        hotelOnly: {
          package: hotelOnlyPkg,
          rate: hotelOnlyRate,
          sell: hotelOnlySell,
          promotion: hotelOnlyPromotion,
          capacityValid: hotelOnlyPackageView.capacityValid,
        },
        bundle: {
          package: bundlePkg,
          rate: bundleRate,
          sell: bundleSell,
          promotion: bundlePromotion,
          capacityValid: hotelOnlyPackageView.capacityValid,
        }
      })
    })

    return rwd
  }, [cheapestPackageSelectionsByRoom, packageViewsByRoom, packageViewsLoading, rooms, canUseMultiRoomUI])

  const hasDates = checkInDate && checkOutDate

  // Same room
  const [{ hotelOnlyRates, hotelBundleRates }, loadingSameRoomRates] = useBedbankRates(offer.id, rooms, checkInDate, checkOutDate)
  const sell = useBedbankOfferSell(offer, duration, rooms)
  const hasRates = hotelOnlyRates.length > 0

  const lowestRate = hotelOnlyRates.reduce((lowest, current) => {
    return lowest.totals.inclusive < current.totals.inclusive ? lowest : current
  }, hotelOnlyRates[0])

  const lowestBundleRate = hotelBundleRates.reduce((lowest, current) => {
    return lowest.totals.inclusive < current.totals.inclusive ? lowest : current
  }, hotelBundleRates[0])

  const showPriceDetails = canUseMultiRoomUI ? !!roomsWithData : ((hasDates && hasRates) || (!hasDates && sell))
  const isSoldOut = canUseMultiRoomUI ? roomsWithData?.length !== rooms.length : hotelOnlyRates.length == 0 && hasDates
  const loading = canUseMultiRoomUI ? packageViewsLoading : loadingSameRoomRates

  return (
    <SummaryPane
      border="neutral-six"
      borderRadius="XS"
      padding="28 20"
      shadow="bottom-small"
      className={className}
      data-testid="SummaryPane"
    >
      <Group direction="vertical" gap={12}>
        <WalledContent
          enforced={offer.walledGarden}
          hidePricing={offer.hidePricing}
          wall={<TextButton kind="primary" fit="flex" onClick={hasDates ? onViewPackageOptions : onCheckAvailabilityClick}>
            {hasDates ? 'View package options' : 'Check availability'}
          </TextButton>}
        >
          {showPriceDetails &&
            <RelativeBlock>
              <LoadingIndicator floating opaque backdrop="grey-light" visible={loadingPrices} />
              {canUseMultiRoomUI && roomsWithData && <BedbankSummaryPriceDetailsIndividualRooms
                roomsWithData={roomsWithData}
                rooms={rooms}
                offer={offer}
                duration={duration}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                onViewPackageOptions={onViewPackageOptions}
                priceTrackedRef={priceTrackedRef}
              />}
              {!canUseMultiRoomUI && <BedbankSummaryPriceDetailsSameRoom
                rate={lowestRate}
                bundleRate={lowestBundleRate}
                rooms={rooms}
                offer={offer}
                duration={duration}
                flightPrice={flightPrice}
                flightsEnabled={flightsEnabled}
                onViewPackageOptions={onViewPackageOptions}
                priceTrackedRef={priceTrackedRef}
              />}
            </RelativeBlock>}
          {!isSoldOut && hasDates && <TextButton kind="primary" fit="flex" onClick={onViewPackageOptions}>
            View package options
          </TextButton>}
          {
            !hasDates && <TextButton kind="primary" fit="flex" onClick={onCheckAvailabilityClick}>Check availability</TextButton>
          }
          {isSoldOut && !loading && <>
            <BodyText
              variant="large"
              weight="bold"
            >
              Sold out on your dates
            </BodyText>
            <TextButton kind="primary" fit="flex" to="/hotels">
              Browse all hotel offers
            </TextButton>
          </>}
        </WalledContent>
      </Group>
    </SummaryPane>
  )
}

export default React.memo(BedbankOfferSummary)
